import { inject } from "fw";
import { dispatch, handle, Store } from "fw-state";
import { debounce } from "lodash-es";
import { StartAction, LogoutAction } from "./actions";
import { FormErrorHandling } from "./error-handling";

import { CalendarEventRepository } from "network/calendar-event-repository";
import { CalendarEventSeriesRepository } from "network/calendar-event-series-repository";

import {
  CalendarEvent,
  CalendarEventSeries,
  cleanCalendarEventSeries,
  cleanUpdateCalendarEventModel,
  CalendarEventInfo,
  CalendarEventSeriesStats
} from "models/calendar-event";
import {
  FormForCalendarEvent,
  FormForUpdateCalendarEventModel,
  FormForCalendarEventSeries
} from "forms/calendar-event";

export class UpdateCalendarEventSeriesAction {
  public updated: CalendarEventSeries = null;
  constructor(public form: FormForCalendarEventSeries) { }
}
export class CalendarEventSeriesUpdatedAction {
  constructor(public updated: CalendarEventSeries) { }
}

export class DeleteCurrentCalendarEventAction {
  constructor(public form: FormForCalendarEvent) { }
}

export class RefreshCalendarSeriesTotalsAction {
  constructor(public force: boolean = false) { }
}

export class FetchCurrentCalendarEventAction {
  public series: CalendarEventSeries = null;
  public instance: CalendarEvent = null;
  public instanceInfos: CalendarEventInfo[] = [];
  constructor(
    public force: boolean = false,
    public seriesId: string = null,
    public instanceKey: string = null
  ) { }
}

export class UpdateCalendarEventAction {
  public updated: CalendarEvent = null;
  constructor(public form: FormForUpdateCalendarEventModel) { }
}

interface CurrentCalendarEventShape {
  loaded: boolean;
  loading: boolean;
  error: boolean;
  series: CalendarEventSeries;
  seriesStats: CalendarEventSeriesStats,
  instance: CalendarEvent;
  instanceInfos: CalendarEventInfo[];
}

@inject
export class CurrentCalendarEventStore extends Store<CurrentCalendarEventShape>  {
  private debouncedRefreshSeriesTotals = async () => { };

  constructor(
    private calendarEventSeriesRepo: CalendarEventSeriesRepository,
    private calendarEventRepo: CalendarEventRepository,
  ) {
    super();
    this.debouncedRefreshSeriesTotals = debounce(() => this.refreshSeriesTotals(), 2000, { maxWait: 5000 });
  }

  defaultState() {
    return {
      loaded: false,
      loading: false,
      error: false,
      series: null,
      seriesStats: null,
      instance: null,
      instanceInfos: [],
    };
  }

  @handle(StartAction)
  @handle(LogoutAction)
  private handleDefaults() {
    this.setState(() => this.defaultState());
  }

  @handle(UpdateCalendarEventSeriesAction, FormErrorHandling)
  private async handleUpdateCalendarEventSeries(action: UpdateCalendarEventSeriesAction) {
    action.form.validate();
    let series = cleanCalendarEventSeries(action.form.updatedModel());
    series = await this.calendarEventSeriesRepo.put(series);
    action.updated = series;
    await dispatch(new CalendarEventSeriesUpdatedAction(series));
  }

  @handle(FetchCurrentCalendarEventAction)
  private async handleFetchCurrentCalendarEvent(action: FetchCurrentCalendarEventAction) {
    if (this.state.loading || !action.seriesId)
      return;

    const { series, instance } = this.state;
    const loaded = (
      action.instanceKey === instance?.instance_key &&
      action.seriesId === series?.id
    );

    if (!action.force && loaded)
      return;

    this.setState(state => ({...state, loading: true }));

    try {
      const seriesInfo = await this.calendarEventSeriesRepo.getInfo(action.seriesId);
      const newInstance = action.instanceKey ? await this.calendarEventRepo.get(action.seriesId, action.instanceKey) : null;
      this.setState(state => ({
        ...state,
        loaded: true,
        loading: false,
        error: false,
        series: seriesInfo.calendar_event_series,
        instance: newInstance,
        instanceInfos: seriesInfo.calendar_event_infos
      }));
      action.series = seriesInfo.calendar_event_series;
      action.instance = newInstance;
      action.instanceInfos = seriesInfo.calendar_event_infos;

    } catch {
      this.setState(state => ({
        ...state,
        loaded: true,
        loading: false,
        error: true,
      }));
    }
    await dispatch(new RefreshCalendarSeriesTotalsAction(action.force));
  }

  @handle(RefreshCalendarSeriesTotalsAction)
  private async handleRefreshCalendarSeriesTotals(action: RefreshCalendarSeriesTotalsAction) {
    if (action.force)
      await this.refreshSeriesTotals();
    else
      await this.debouncedRefreshSeriesTotals();
  }

  private async refreshSeriesTotals() {
    const seriesId = this.state.series?.id;
    if (!seriesId)
      return;
    
    const seriesStats = await this.calendarEventSeriesRepo.getCounts(seriesId);
    this.setState(state => ({
      ...state,
      seriesStats
    }));
  }

  @handle(UpdateCalendarEventAction)
  private async handleUpdateCalendarEvent(action: UpdateCalendarEventAction) {
    if (!action.form)
      return;

    action.form.validate();
    const updateModel = cleanUpdateCalendarEventModel(action.form.updatedModel());
    const updated = await this.calendarEventRepo.put(updateModel)
    action.updated = updated;
    const seriesInfo = await this.calendarEventSeriesRepo.getInfo(updated.calendar_event_series_id);
    this.setState(state => ({...state,
      loaded: true,
      loading: false,
      error: false,
      series: seriesInfo.calendar_event_series,
      instance: updated,
      instanceInfos: seriesInfo.calendar_event_infos,
    }));
  }

  @handle(DeleteCurrentCalendarEventAction)
  private async handleDeleteCurrentCalendarEvent(action: DeleteCurrentCalendarEventAction) {
    if (!action.form) return;
    const { calendar_event_series_id, instance_key } = action.form;
    if (!calendar_event_series_id || !instance_key)
      return;
    await this.calendarEventRepo.delete(calendar_event_series_id, instance_key);
    await this.refreshSeriesTotals();
  }
}
